import { useCallback, useEffect, useState } from "react";

import type { HeartRateDataPoint } from "~/api/heartRate";
import {
  getDateRange,
  getGroupByInterval,
  getHeartRateData,
} from "~/api/heartRate";

interface UseHeartRateDataProps {
  patientId: number;
  timeRange: string;
  navigationOffset?: number;
  selectedDate?: Date;
}

interface UseHeartRateDataReturn {
  data: HeartRateDataPoint[];
  metadata: {
    average: number;
    min: number;
    max: number;
  } | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useHeartRateData = ({
  patientId,
  timeRange,
  navigationOffset = 0,
  selectedDate,
}: UseHeartRateDataProps): UseHeartRateDataReturn => {
  const [data, setData] = useState<HeartRateDataPoint[]>([]);
  const [metadata, setMetadata] = useState<{
    average: number;
    min: number;
    max: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const dateRange = getDateRange(timeRange, navigationOffset, selectedDate);
      const groupByInterval = getGroupByInterval(timeRange);


      const response = await getHeartRateData({
        patient_id: patientId,
        group_by_interval: groupByInterval,
        start_date: dateRange.start_date,
        stop_date: dateRange.stop_date,
      });

      setData(response.data);
      setMetadata(response.metadata);
    } catch (err) {
      console.error("Error fetching heart rate data:", err);
      setError("Failed to fetch heart rate data");
      setData([]);
      setMetadata(null);
    } finally {
      setLoading(false);
    }
  }, [patientId, timeRange, navigationOffset, selectedDate]);

  useEffect(() => {
    if (patientId) {
      void fetchData();
    }
  }, [patientId, fetchData]);

  return {
    data,
    metadata,
    loading,
    error,
    refetch: fetchData,
  };
};
